import { switchConnection } from '@wagmi/core'
import { Provider } from 'accounts'
import { useConnectors } from 'wagmi'

import { wagmiConfig } from './config.js'

/** Returns the WebAuthn (passkey) connector. */
export function useWebAuthnConnector() {
  const connectors = useConnectors()
  return connectors.find((c) => c.type === 'webAuthn')!
}

/** Returns the Tempo Wallet (iframe dialog) connector. */
export function useDialogConnector() {
  const connectors = useConnectors()
  return connectors.find((c) => c.type === 'injected' && c.id === 'xyz.tempo')!
}

/**
 * Mirror webAuthn provider state to the dialog provider, and keep the webAuthn
 * connector as the active wagmi connection whenever it's connected. Runs in
 * both parent and iframe contexts so signing always uses the local passkey
 * instead of round-tripping through the dialog connector.
 */
export async function sync() {
  const [webAuthnConnector, dialogConnector] = wagmiConfig.connectors
  if (!webAuthnConnector || !dialogConnector) return

  const source = (await webAuthnConnector.getProvider()) as Provider.Provider
  const target = (await dialogConnector.getProvider()) as Provider.Provider

  function syncStore() {
    const { accounts, activeAccount, chainId } = source.store.getState()
    target.store.setState({ accounts, activeAccount, chainId })
  }

  function syncWebAuthn() {
    const connection = wagmiConfig.state.connections.get(webAuthnConnector!.uid)
    if (!connection) return
    if (wagmiConfig.state.current === webAuthnConnector!.uid) return
    switchConnection(wagmiConfig as never, { connector: webAuthnConnector as never }).catch(
      () => {},
    )
  }

  syncStore()
  source.store.subscribe(syncStore)

  syncWebAuthn()
  wagmiConfig.subscribe((state) => state.connections, syncWebAuthn)
}

if (typeof window !== 'undefined') sync()
